import React, { useCallback, useEffect, useRef, useState } from 'react';
import { MessageType } from '@common/model/dialog/message/messageType';
import { ActionButton } from '@cp/ds/src/components/actionButton';
import { CopyIcon } from '@cp/ds/src/icons';
import { copyTextToClipboard } from '@cp/shared/clipboard';
import {
  DialogMessageRenderer,
  DialogMessageText,
  MessageDate,
  MessageDelivery,
  MessageFooter,
  MessageHeader,
  TextMessageLayout,
  useIsOutgoing,
  useReadNotification,
} from '@cp/entities/dialog';

const COPY_TEXT = 'Копировать';
const COPIED_TEXT = 'Скопировано!';
const COPY_HINT_RESET_DELAY = 2000;

export const DialogTextMessageWithCopy: DialogMessageRenderer<MessageType.Text> = ({ message, isPreview }) => {
  const { payload } = message;
  const outgoing = useIsOutgoing(message);
  const ref = useRef<HTMLDivElement>(null);
  const [hint, setHint] = useState(COPY_TEXT);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useReadNotification(ref, message);

  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  const handleCopy = useCallback(() => {
    if (!payload) {
      return;
    }

    copyTextToClipboard(payload);
    setHint(COPIED_TEXT);
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    timeoutRef.current = setTimeout(() => setHint(COPY_TEXT), COPY_HINT_RESET_DELAY);
  }, [payload]);

  if (isPreview) {
    return <DialogMessageText payload={payload} textOnly={isPreview} />;
  }

  return (
    <TextMessageLayout id={message.draftId} outgoing={outgoing} ref={ref} sx={{ userSelect: 'text' }}>
      <MessageHeader style={{ display: outgoing ? 'none' : undefined }}>{message.author.name}</MessageHeader>
      <DialogMessageText payload={payload} />
      <MessageFooter>
        <ActionButton disabled={!payload} hint={payload ? hint : undefined} onClick={handleCopy} sx={{ p: 0.25, color: 'inherit' }}>
          <CopyIcon fontSize="small" />
        </ActionButton>
        <MessageDelivery message={message} />
        <MessageDate message={message} />
      </MessageFooter>
    </TextMessageLayout>
  );
};
